import { motion, useScroll } from 'framer-motion';
import { useRef } from 'react';
import { WordsPullUpMultiStyle, ScrollAnimatedText } from '../components/AnimationComponents';

export const AboutSection = () => {
  const textRef = useRef(null);

  const { scrollYProgress } = useScroll({
    target: textRef,
    offset: ['start 0.8', 'end 0.2']
  });

  const aboutText = "The Hygiene Company was started with the belief that good quality cleanliness and hygiene should reach everyone — with affordable prices. Our work combines affordable hygiene solutions with social awareness initiatives like The Great Hygiene Drive, helping spread awareness about cleanliness, sanitation, and healthy living. We are currently working toward building a trusted hygiene ecosystem for institutions, workplaces, and communities across India. We are a new-age hygiene brand focused on one simple mission: improving everyday hygiene standards without making it expensive. At The Hygiene Company, we are building a growing ecosystem of hygiene solutions — from essential cleaning products to institution-focused hygiene support services. Behind this mission is a dedicated team of young minds working with one vision: Better hygiene for every space, every person, every day.";

  return (
    <section id="about" className="min-h-screen py-12 md:py-20 px-4 md:px-8" data-testid="about-section">
      <div className="max-w-6xl mx-auto">
        {/* Header Card with glassmorphism */}
        <div ref={textRef} className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-3xl p-8 md:p-12 lg:p-16 shadow-2xl">
          <p className="text-white text-center text-[10px] sm:text-xs font-bold mb-6 md:mb-8 tracking-widest uppercase drop-shadow">
            Our Story — Building a Hygiene Ecosystem
          </p>

          <WordsPullUpMultiStyle
            segments={[
              { text: "Who", className: "text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-normal text-white drop-shadow-lg" },
              { text: "We", className: "text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-normal text-[#87CEEB] drop-shadow-lg" },
              { text: "Are", className: "text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-normal text-[#90EE90] drop-shadow-lg" }
            ]}
            containerClassName="text-center max-w-3xl mx-auto leading-[0.95] sm:leading-[0.9] mb-12 md:mb-16"
          />

          <ScrollAnimatedText
            text={aboutText}
            scrollProgress={scrollYProgress}
            className="text-white text-sm sm:text-base md:text-lg text-center max-w-4xl mx-auto leading-relaxed drop-shadow"
          />

          <motion.div
            initial={{ y: 30, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.5 }}
            className="mt-12 md:mt-16 text-center"
          >
            <p className="font-serif italic text-white text-xl sm:text-2xl md:text-3xl drop-shadow-lg">
              "Better hygiene for every space, every person, every day."
            </p>
          </motion.div>
        </div>

        {/* Values Section */}
        <motion.div
          initial={{ y: 50, opacity: 0 }}
          whileInView={{ y: 0, opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-12"
        >
          {[
            { title: "Affordable", color: "#87CEEB", desc: "Quality hygiene shouldn't be expensive" },
            { title: "Accessible", color: "#90EE90", desc: "Reaching every community, everywhere" },
            { title: "Impactful", color: "#FFFFFF", desc: "Creating real change at grassroots level" }
          ].map((value, i) => (
            <motion.div
              key={i}
              initial={{ y: 20, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: i * 0.2 }}
              className="bg-white/15 backdrop-blur-md border border-white/30 rounded-2xl p-8 text-center hover:scale-105 hover:bg-white/25 transition-all duration-300"
            >
              <h3 
                className="text-2xl font-bold mb-3 drop-shadow-lg"
                style={{ color: value.color }}
              >
                {value.title}
              </h3>
              <p className="text-white/80 text-sm">
                {value.desc}
              </p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};
